import React from 'react';

import { ButtonProps, Grid, TextFieldProps } from '@ha/dsl';

import { DateRangePickerInputsWrapper } from './DateRangePickerInputsWrapper';
import { Calendar } from '../Calendar/Calendar';
import { CalendarFooter } from '../Calendar/CalendarFooter';
import { CalendarInfo } from '../Calendar/CalendarInfo';
import {
  DatePickerInputsProps,
  DateRangePickerProps,
  FOCUSED_FIELD,
  OverlayProps,
  DayPickerProps,
  TranslateFunc,
  FormatDate,
  DateRange,
} from '../types';

interface Props {
  AdditionalInfo?: React.ReactNode;
  autoClose: boolean;
  buttonProps: ButtonProps;
  noEndDate: boolean;
  noEndDateLabel: string;
  CaptionSubtitle?: DateRangePickerProps['CaptionSubtitle'];
  DatePickerInputs: React.ComponentType<DatePickerInputsProps>;
  datePickerProps: DayPickerProps;
  focused: FOCUSED_FIELD | null;
  from?: Date;
  fromEmptyValueLabel: string;
  to?: Date;
  onDatesClear?: () => void;
  onNoEndDate: () => void;
  onOverlayExit: () => void;
  onOverlayOpen: (field: FOCUSED_FIELD) => void;
  overlayDoneBtnLabel: string;
  clearDatesButton?: string;
  fromInputProps: TextFieldProps;
  minimumPeriod?: number;
  maximumPeriod?: number;
  moveInWindows: DateRange[];
  moveInWindowsTooltip: string;
  toInputProps: TextFieldProps;
  toEmptyValueLabel: string;
  Overlay: React.FC<OverlayProps>;
  header?: React.ReactNode;
  T: TranslateFunc;
  formatDate: FormatDate;
  withoutDividers: boolean;
  dialogTitle?: string;
  onOverlayDone?: (range: DateRange) => void;
  onOverlayCancel?: () => void;
  fullScreenDialog?: boolean;
  showCancelButton?: boolean;
  localeString: string;
}

const DateRangePickerOverlay: React.FC<Props> = ({
  AdditionalInfo,
  autoClose,
  buttonProps,
  noEndDate,
  noEndDateLabel,
  CaptionSubtitle,
  DatePickerInputs,
  focused,
  from,
  to,
  onDatesClear,
  onNoEndDate,
  onOverlayExit,
  onOverlayOpen,
  Overlay,
  overlayDoneBtnLabel,
  clearDatesButton,
  fromInputProps,
  toInputProps,
  fromEmptyValueLabel,
  toEmptyValueLabel,
  datePickerProps,
  minimumPeriod,
  maximumPeriod,
  moveInWindows,
  moveInWindowsTooltip,
  header,
  T,
  formatDate,
  withoutDividers,
  dialogTitle,
  onOverlayDone,
  onOverlayCancel,
  fullScreenDialog,
  showCancelButton,
  localeString,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (open && !focused && autoClose) {
      setOpen(false);
    }
  }, [focused, open]);

  const onOverlayOpenHandler = React.useCallback(
    (field: FOCUSED_FIELD) => {
      setOpen(true);
      onOverlayOpen(field);
    },
    [onOverlayOpen],
  );

  const onOverlayCloseHandler = React.useCallback(() => {
    if (onOverlayCancel) {
      onOverlayCancel();
    }
    setOpen(false);
  }, [onOverlayCancel]);

  const onOverlayDoneHandler = React.useCallback(() => {
    if (onOverlayDone) {
      onOverlayDone({ from, to });
    }
    setOpen(false);
  }, [onOverlayDone, from, to]);

  const onNoEndDateHandler = React.useCallback(() => {
    onNoEndDate();
    setOpen(false);
  }, [onNoEndDate]);

  return (
    <Grid container justifyContent="center" ref={ref}>
      <DateRangePickerInputsWrapper
        focused={focused}
        from={from}
        fromEmptyValueLabel={fromEmptyValueLabel}
        fromInputProps={fromInputProps}
        onFieldFocusChange={onOverlayOpenHandler}
        to={to}
        toEmptyValueLabel={toEmptyValueLabel}
        toInputProps={toInputProps}
        formatDate={formatDate}
      >
        {inputsProps => <DatePickerInputs {...inputsProps} />}
      </DateRangePickerInputsWrapper>
      <Overlay
        containerRef={ref}
        focused={focused}
        from={from}
        onFieldFocusChange={onOverlayOpen}
        onOverlayExit={onOverlayExit}
        onOverlayClose={onOverlayCloseHandler}
        open={open}
        withoutDividers={withoutDividers}
        dialogTitle={dialogTitle}
        OverlayTitle={header}
        fullScreenDialog={fullScreenDialog}
        OverlayActions={
          <CalendarFooter
            buttonProps={buttonProps}
            clearDatesButton={clearDatesButton}
            onClearDates={onDatesClear}
            onNoEndDate={onNoEndDateHandler}
            onOverlayClose={onOverlayCloseHandler}
            overlayCancelBtnLabel={T('Cancel')}
            showCancelButton={showCancelButton}
            onOverlayDone={onOverlayDoneHandler}
            showOverlayDoneButton={!autoClose}
            showNoEndDateButton={focused === FOCUSED_FIELD.TO && noEndDate}
            overlayDoneBtnLabel={overlayDoneBtnLabel}
            noEndDateButton={noEndDateLabel}
            fullScreenDialog={fullScreenDialog}
          />
        }
        OverlayInfo={
          <CalendarInfo
            additionalInfo={AdditionalInfo}
            minimumPeriod={minimumPeriod}
            maximumPeriod={maximumPeriod}
            hasMoveInWindowHint={moveInWindows.length > 0}
            T={T}
          />
        }
        to={to}
        formatDate={formatDate}
        T={T}
      >
        <Calendar
          {...datePickerProps}
          captionSubtitle={CaptionSubtitle}
          highlightMoveInWindows={focused === FOCUSED_FIELD.FROM}
          moveInWindows={moveInWindows}
          moveInWindowsTooltip={moveInWindowsTooltip}
          usePrevMonth={focused === null && autoClose}
          localeString={localeString}
        />
      </Overlay>
    </Grid>
  );
};

export { DateRangePickerOverlay };
